import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import b from 'b_';
import PageRestrictionsStore, { EXIT_OPTION } from '../../../stores/Policy/ContentRules/PageRestrictionsStore';
import { FTBody } from '../../Fuse';
import Card from '../../Fuse/Card';
import FuseReadOnlyRadioButton from '../../FuseControls/FuseReadOnlyRadioButton';
import FuseReadOnlySwitch from '../../FuseControls/FuseReadOnlySwitch';
import ConsoleField from '../../ConsoleFragments/ConsoleField';
import PasteLimitField from '../../DataLossPrevention/PasteLimitField';
import FuseInfoTooltip from '../../FuseControls/FuseTooltip/FuseInfoTooltip';
import Icon from '../../Icon/Icon';
import PolicySection from '../PolicySection';
import FuseConsoleCardFooter from '../../ConsoleFragments/FuseConsoleCardFooter';
import './PageRestrictions.css';

const block = b.lock('PageRestrictions');
const maxPasteLimitB = b.lock('PageRestrictions__MaxPaste');

export const EXIT_URL_TEXT = {
    [EXIT_OPTION.NONE]: "No 'Exit' option",
    [EXIT_OPTION.AUTO]: <Fragment>
        Automatic exit after scan -
        <Icon className={block('ExitAutoWarningIcon')} type={'warning'}/>
        Not Recommended
    </Fragment>,
    [EXIT_OPTION.WITH_MSG]: "Display 'Exit' option with warning message",
    [EXIT_OPTION.WITHOUT_MSG]: "Display 'Exit' option without warning message",
};

export const EXIT_URL_TOOLTIPS = {
    [EXIT_OPTION.WITH_MSG]: `Display an 'Exit' option. Clicking Exit will display a warning message with
        'Stay Protected' and 'Exit' controls. Exit will redirect the current URL to the native browser.
         When Reputation and Threat scans are enabled, the Exit option will only display if no threats are detected.`,
    [EXIT_OPTION.WITHOUT_MSG]: `Display an 'Exit' option.
        Clicking Exit opens the redirected URL to the native browser.
        When Reputation and Threat scans are enabled,
        the Exit option will only display if no threats are detected.`,
    [EXIT_OPTION.AUTO]: `When Reputation and Threat scans are enabled and no threats are detected,
        the user's Isolation session will end, and the redirected URL will automatically
        open in the native browser and will be subject to any
        customer networking configurations.`,
    [EXIT_OPTION.NONE]: 'The \'Exit\' option is not displayed to the user.',
};

class PageRestrictions extends Component {
    static propTypes = {
        store: PageRestrictionsStore.PropType.isRequired,
        readOnly: PropTypes.bool,
        showExitIsoOption: PropTypes.bool.isRequired,
        hasUploadDownload: PropTypes.bool.isRequired,
        showDLP: PropTypes.bool.isRequired,
        urlIsolation: PropTypes.bool,
        useFileThreatRules: PropTypes.bool,
    };

    componentDidUpdate(prevProps) {
        if (prevProps.urlIsolation && !this.props.urlIsolation) {
            this.props.store.allowExitIso.setFalse();
        }
    }

    render() {
        const { store, readOnly, showExitIsoOption, hasUploadDownload, showDLP, useFileThreatRules } = this.props;
        const switchProps = {
            tooltipProps: { position: 'right' },
            className: block('SwitchContainer'),
            readOnly
        };
        const rowProps = {
            className: block('SectionsRow')
        };
        const switchContainerProps = {
            className: block('SwitchesContainer')
        };

        return <Card title="Page Controls">
            <div {...rowProps}>
                <PolicySection lastInRow>
                    <FTBody className={block('Title')}>Threat Scanning</FTBody>
                    <div {...switchContainerProps}>
                        <FuseReadOnlySwitch
                            header="Reputation Check"
                            tooltip={`Check the current URL and domain reputation against Proofpoint Threat
                            Intelligence data to determine if it should be blocked due to an emerging or existing
                            threat.`}
                            store={store.reputationCheck}
                            label={store.reputationCheck.value ? 'Enabled' : 'Disabled'}
                            qa="reputation-switch"
                            {...switchProps}
                        />
                        <FuseReadOnlySwitch
                            header="Threat Scan"
                            tooltip={`Check the contents of the URL destination, Document Object Model (DOM) or file,
                            in real-time to determine if there is a Phishing or Malware threat present using
                            Proofpoint Advanced Threat Protection.`}
                            store={store.threatScanning}
                            label={store.threatScanning.value ? 'Enabled' : 'Disabled'}
                            qa="threat-switch"
                            {...switchProps}
                        />
                    </div>
                </PolicySection>
            </div>

            <div {...rowProps}>
                <PolicySection lastInRow>
                    <FTBody className={block('Title')}>Input Controls</FTBody>
                    <div {...switchContainerProps}>
                        <FuseReadOnlySwitch
                            header="All Input"
                            tooltip="All input includes keyboard and clipboard paste operations."
                            store={store.allInput}
                            label={store.allInput.isTrue ? 'Enabled' : 'Disabled'}
                            qa="all-input-switch"
                            {...switchProps}
                        />

                        {store.allInput.isTrue
                            ? <FuseReadOnlySwitch
                                header="Clipboard Paste"
                                tooltip="Optionally disable clipboard paste operations."
                                store={store.clipboardPaste}
                                label={store.clipboardPaste.isTrue ? 'Enabled' : 'Disabled'}
                                qa="clipboard-switch"
                                {...switchProps}
                            /> : null}

                        {store.clipboardPaste.isTrue
                            ? <FuseReadOnlySwitch
                                header="Paste Limit"
                                tooltip="Optionally enable a character limit for a pasted content operation."
                                store={store.pasteLimitEnabled}
                                label={store.pasteLimitEnabled.isTrue ? 'Enabled' : 'Disabled'}
                                readOnlyValue={store.pasteLimitEnabled.isTrue
                                    ? `Enabled (${store.pasteLimit.value} characters)`
                                    : 'Disabled'}
                                qa="paste-switch"
                                {...switchProps}
                            /> : null}

                        {store.pasteLimitEnabled.isTrue && !readOnly ?
                            <ConsoleField
                                className={block('MaxLimitContainer')}
                                titleClassName={block('MaxLimitInputContainer')}
                                name="Paste Limit Maximum"
                                info="Specify the maximum number of characters a user can paste in a single operation."
                                infoPosition="left"
                            >
                                <PasteLimitField
                                    store={store.pasteLimit}
                                    info={'Characters'}
                                    fieldCls={maxPasteLimitB()}
                                    infoCls={maxPasteLimitB('Info')}
                                    inputCls={maxPasteLimitB('Input')}
                                />
                            </ConsoleField> : null}
                        <FuseConsoleCardFooter
                            className={block('SectionFooter')}
                            iconClassName={block('SectionFooterIcon')}
                            text={<div className={block('FooterText')}>If multiple Content Rules are enforced on a
                                page with embedded HTML content, the system will enforce the most restrictive
                                rule’s Input Controls on the entire page.</div>}
                        />
                    </div>
                </PolicySection>
            </div>


            {hasUploadDownload && !useFileThreatRules ? <div {...rowProps}>
                <PolicySection lastInRow>
                    <FTBody className={block('Title')}>Upload Controls</FTBody>
                    <div {...switchContainerProps}>
                        <FuseReadOnlySwitch
                            header="Allow Uploads"
                            tooltip="Allow a file upload operation with the option to perform additional scans."
                            store={store.allowUploads}
                            label={store.allowUploads.isTrue ? 'Enabled' : 'Disabled'}
                            qa="uploads-switch"
                            {...switchProps}
                        />
                        {store.allowUploads.isTrue
                            ? <Fragment>
                                <FuseReadOnlySwitch
                                    header="File Threat Scan"
                                    tooltip={`Scan the content of each file for threats and block the operation
                                    upon threat detection.`}
                                    store={store.uploadsFileThreatScan}
                                    label={store.uploadsFileThreatScan.isTrue ? 'Enabled' : 'Disabled'}
                                    qa="file-threat-upload-switch"
                                    {...switchProps}
                                />
                                {showDLP ? <FuseReadOnlySwitch
                                    header="File DLP Scan"
                                    tooltip={`Scan the content of each file using the Data Loss Protection Inline
                                        Rules in your Proofpoint CASB service to control the file transfer.
                                        If there is no matching Inline Rule, the operation will be allowed.
                                        If there is a matching Inline Rule, the defined Inline Rule
                                        operation of Block or Allow will be followed.`}
                                    store={store.uploadsFileDlpScan}
                                    label={store.uploadsFileDlpScan.isTrue ? 'Enabled' : 'Disabled'}
                                    qa="file-dlp-upload-switch"
                                    {...switchProps}
                                /> : null}
                            </Fragment> : null}
                    </div>
                </PolicySection>
            </div> : null }

            {!useFileThreatRules && <div {...rowProps}>
                <PolicySection lastInRow>
                    <FTBody className={block('Title')}>Download Controls</FTBody>
                    <div {...switchContainerProps}>
                        <FuseReadOnlySwitch
                            header="Allow Downloads"
                            tooltip={'Allow a file download operation ' +
                            'with the option to perform additional scans.'}
                            store={store.allowDownloads}
                            label={store.allowDownloads.isTrue ? 'Enabled' : 'Disabled'}
                            qa="downloads-switch"
                            {...{
                                ...switchProps,
                                // doesn't have upload/download
                                ...(!hasUploadDownload && {
                                    readOnlyValue: 'Disabled in this version',
                                    readOnly: true
                                }),
                            }}
                        />
                        {store.allowDownloads.isTrue
                            ? <Fragment>
                                <FuseReadOnlySwitch
                                    header="File Threat Scan"
                                    tooltip={`Scan the content of each file for threats and block the operation
                                    upon threat detection.`}
                                    store={store.downloadsFileThreatScan}
                                    label={store.downloadsFileThreatScan.isTrue ? 'Enabled' : 'Disabled'}
                                    qa="file-threat-download-switch"
                                    {...switchProps}
                                />
                                {showDLP ? <FuseReadOnlySwitch
                                    header="File DLP Scan"
                                    tooltip={`Scan the content of each file using the Data Loss Protection Inline
                                        Rules in your Proofpoint CASB service to control the file transfer.
                                        If there is no matching Inline Rule, the operation will be allowed.
                                        If there is a matching Inline Rule, the defined Inline Rule
                                        operation of Block or Allow will be followed.`}
                                    store={store.downloadsFileDlpScan}
                                    label={store.downloadsFileDlpScan.isTrue ? 'Enabled' : 'Disabled'}
                                    qa="file-dlp-download-switch"
                                    {...switchProps}
                                /> : null}
                            </Fragment> : <FuseReadOnlySwitch
                                header="Safely Preview"
                                tooltip={`Allow users to safely view blocked downloads
                                    in an Isolated Preview (supported file types only).`}
                                store={store.allowFilePreview}
                                label={store.allowFilePreview.isTrue ? 'Enabled' : 'Disabled'}
                                qa="file-preview-switch"
                                {...switchProps}
                            />}
                    </div>
                </PolicySection>
            </div> }

            {this.props.urlIsolation && showExitIsoOption
                ? <div {...rowProps}>
                    <PolicySection lastInRow>
                        <FTBody className={block('Title')}>
                            Exit URL Isolation <span className={block('Subtitle')}>(URL Isolation Only)</span>
                        </FTBody>
                        <div className={block('ExitOptionsContainer')}>
                            {Object.values(EXIT_OPTION).map((opt) => <FuseReadOnlyRadioButton
                                key={opt}
                                value={opt}
                                readOnly={readOnly}
                                className={block('RadioButton')}
                                store={store.exitOption}
                                label={<span className={block('ExitTooltipContainer')}>
                                    {EXIT_URL_TEXT[opt]}
                                    <FuseInfoTooltip className={block('ExitTooltip')}>
                                        {EXIT_URL_TOOLTIPS[opt]}
                                    </FuseInfoTooltip>
                                </span>}
                                qa={`exit-${opt}`}
                            />)}
                        </div>
                        <FuseConsoleCardFooter
                            className={block('SectionFooter')}
                            iconClassName={block('SectionFooterIcon')}
                            text={<div className={block('FooterText')}>The Exit option is always determined by the
                                Content Rule enforced on the address bar URL.</div>}
                        />
                    </PolicySection>
                </div> : null}
        </Card>;
    }
}

export default observer(PageRestrictions);
